/**
 * 邮件任务筛选条件构建工具
 *
 * 将前端 UI 上的筛选条件（分类、国家、关键词、是否有邮箱）转换为后端 API
 * 所需的查询参数对象。仅包含非空/非默认的条件字段，避免发送冗余参数。
 *
 * 导出内容：
 * - `CriteriaInput` 接口：描述筛选条件的输入结构
 * - `buildCriteriaPayload` 函数：将输入转换为 API 查询参数
 *
 * 使用场景：邮件任务列表页的搜索/筛选功能
 */

/**
 * 筛选条件输入接口
 */
export interface CriteriaInput {
  /** 分类 ID（0 表示不筛选） */
  criteriaCategoryId: number;
  /** 国家 ID（0 表示不筛选） */
  criteriaNationId: number;
  /** 搜索关键词（空字符串表示不筛选） */
  criteriaKeyword: string;
  /** 是否仅显示包含邮箱的客户 */
  criteriaHasEmail: boolean;
}

/**
 * 将筛选条件转换为后端 API 查询参数
 *
 * 仅包含有实际值的字段（非零 ID、非空关键词），以减少请求体大小。
 * `has_email` 字段仅在用户勾选时发送，值为 1。
 *
 * @param input - 筛选条件输入
 * @returns 后端 API 所需的查询参数对象（snake_case 键名）
 */
export function buildCriteriaPayload(input: CriteriaInput): Record<string, any> {
  const out: Record<string, any> = {};
  // 分类 ID 非零时才添加（0 是默认值，表示不筛选）
  if (input.criteriaCategoryId) out.category_id = input.criteriaCategoryId;
  // 国家 ID 非零时才添加
  if (input.criteriaNationId) out.nation_id = input.criteriaNationId;
  // 关键词去除首尾空格后非空才添加
  const kw = input.criteriaKeyword.trim();
  if (kw) out.keyword = kw;
  // 仅在勾选"有邮箱"时发送，后端用 1 表示 true
  if (input.criteriaHasEmail) out.has_email = 1;
  return out;
}
